"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";
import { CMSButtonClient } from "@/components/cms/CMSButton";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";
import { motion } from "framer-motion";

export default function Sport({ cms }: { cms?: Record<string, string> }) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  
  // Use optimized parallax hook
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);
  
  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  const DEFAULT_SPORTS: { src: string; alt: string }[] = [];
  let sports: { src: string; alt: string }[] = DEFAULT_SPORTS;
  try {
    if (cms?.sports_list) sports = JSON.parse(cms.sports_list);
  } catch {
    // use default
  }

  return (
    <section className="pt-6 pb-0 sm:pt-16 sm:pb-16 school-purple text-white relative overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        ref={parallaxRef}
        className={`absolute inset-0 will-change-transform top-0 h-full sm:top-[-55%] sm:h-[190%]`}
        style={mounted && isDesktop ? { transform: transform3d } : { transform: 'translate3d(0, 0, 0)' }}
      >
        {cms?.bg_image_url && <Image
          src={cms.bg_image_url}
          alt="Sports team background"
          fill
          className="object-cover object-[center_top]"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
        />}
      </div>
      {cms?.overlay_color && <div className="absolute inset-0" style={{ backgroundColor: cms.overlay_color }}></div>}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-0 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px 0px -100px 0px" }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-2">
            {cms?.heading_line1 /* || "Ashton John\u2019s" */}
          </h2>
          <h3 className="text-5xl sm:text-6xl md:text-7xl font-bold">{cms?.heading_line2 /* || "SPORT" */}</h3>
          <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed mt-8 max-w-4xl mx-auto">
            {cms?.subtitle /* || "Sport allows students an opportunity to express themselves and connect with other students." */}
          </p>
        </motion.div>

        {/* Custom grid for mobile: 3 columns, 2 rows; E-Sport in center top */}
        {sports.length >= 5 && <div className="grid grid-cols-3 grid-rows-2 sm:grid-cols-5 sm:grid-rows-1 gap-8 mb-16">
          {/* Always 5 icons in a row at sm and up, 3x2 on mobile */}
          {/* Soccer */}
          <div className="text-center flex flex-col items-center sm:col-start-1 sm:row-start-1">
            <div className="w-20 sm:w-25 md:w-30 lg:w-35 aspect-square relative mb-4">
              <Image
                src={sports[0].src}
                alt={sports[0].alt}
                fill
                className="rounded-lg object-cover"
                sizes="(max-width: 639px) 80px, (max-width: 767px) 100px, (max-width: 1023px) 120px, 140px"
              />
            </div>
            <h3 className="text-xl font-bold text-school-gold">
              {sports[0].alt}
            </h3>
          </div>
          {/* E-Sport */}
          <div className="text-center flex flex-col items-center sm:col-start-2 sm:row-start-1">
            <div className="w-20 sm:w-25 md:w-30 lg:w-35 aspect-square relative mb-4">
              <Image
                src={sports[4].src}
                alt={sports[4].alt}
                fill
                className="rounded-lg object-contain p-2"
                sizes="(max-width: 639px) 80px, (max-width: 767px) 100px, (max-width: 1023px) 120px, 140px"
              />
            </div>
            <h3 className="text-xl font-bold text-school-gold">
              {sports[4].alt}
            </h3>
          </div>
          {/* Netball */}
          <div className="text-center flex flex-col items-center sm:col-start-3 sm:row-start-1">
            <div className="w-20 sm:w-25 md:w-30 lg:w-35 aspect-square relative mb-4">
              <Image
                src={sports[1].src}
                alt={sports[1].alt}
                fill
                className="rounded-lg object-cover"
                sizes="(max-width: 639px) 80px, (max-width: 767px) 100px, (max-width: 1023px) 120px, 140px"
              />
            </div>
            <h3 className="text-xl font-bold text-school-gold">
              {sports[1].alt}
            </h3>
          </div>
          {/* Athletics */}
          <div className="text-center flex flex-col items-center sm:col-start-4 sm:row-start-1">
            <div className="w-20 sm:w-25 md:w-30 lg:w-35 aspect-square relative mb-4">
              <Image
                src={sports[2].src}
                alt={sports[2].alt}
                fill
                className="rounded-lg object-contain p-2"
                sizes="(max-width: 639px) 80px, (max-width: 767px) 100px, (max-width: 1023px) 120px, 140px"
              />
            </div>
            <h3 className="text-xl font-bold text-school-gold">
              {sports[2].alt}
            </h3>
          </div>
          {/* Chess */}
          <div className="text-center flex flex-col items-center sm:col-start-5 sm:row-start-1">
            <div className="w-20 sm:w-25 md:w-30 lg:w-35 aspect-square relative mb-4">
              <Image
                src={sports[3].src}
                alt={sports[3].alt}
                fill
                className="rounded-lg object-contain p-2"
                sizes="(max-width: 639px) 80px, (max-width: 767px) 100px, (max-width: 1023px) 120px, 140px"
              />
            </div>
            <h3 className="text-xl font-bold text-school-gold">
              {sports[3].alt}
            </h3>
          </div>
          {/* View Gallery button in center cell for mobile, hidden on sm+ */}
          <div className="row-start-2 col-start-2 flex justify-center items-center sm:hidden">
            <CMSButtonClient
              type="gallery"
              className="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
            >
              VIEW GALLERY
              <span className="sr-only">
                View sports gallery at Ashton John's Private School
              </span>
            </CMSButtonClient>
          </div>
        </div>}

        {/* CTA Buttons for sm+ only */}
        <div className="hidden sm:flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <CMSButtonClient
            type="gallery"
            className="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          >
            VIEW GALLERY
            <span className="sr-only">
              View sports gallery at Ashton John's Private School
            </span>
          </CMSButtonClient>
        </div>
      </div>
    </section>
  );
}
